import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SampleManagerLookupComponent } from '@shared/sample-manager-lookup/sample-manager-lookup.component';
import { CisProLookupDto } from '@models/cis-pro-lookup-dto.interface';
import { DesignService } from '../../../../../services/design.service';

export interface FormulationBarcodeDialogData {
  jobRequestId: number;
  formulationComponentId: number;
  componentName: string | null;
  currentBarcode: string | null;
}

@Component({
  selector: 'app-formulation-barcode-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    SampleManagerLookupComponent,
  ],
  templateUrl: './formulation-barcode-dialog.component.html',
  styleUrl: './formulation-barcode-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormulationBarcodeDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<FormulationBarcodeDialogComponent, boolean>);
  protected readonly data = inject<FormulationBarcodeDialogData>(MAT_DIALOG_DATA);
  private readonly designService = inject(DesignService);

  protected readonly lookupResult = signal<CisProLookupDto | null>(null);
  protected readonly saveError = signal<string | null>(null);
  protected readonly isSaving = signal(false);

  protected onLookupResult(result: CisProLookupDto): void {
    this.lookupResult.set(result);
    this.saveError.set(null);
  }

  protected onLookupError(_message: string): void {
    this.lookupResult.set(null);
    this.saveError.set(null);
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }

  protected onAccept(): void {
    const barcode = this.lookupResult()?.barcode?.trim();
    if (!barcode || this.isSaving()) {
      return;
    }

    this.saveError.set(null);
    this.isSaving.set(true);

    this.designService
      .assignFormulationComponentBarcode(
        this.data.jobRequestId,
        this.data.formulationComponentId,
        { barcode }
      )
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.dialogRef.close(true);
        },
        error: (err: HttpErrorResponse) => {
          const validationMessage = err.error?.errors
            ? Object.values(err.error.errors)
                .flat()
                .join(' ')
            : null;

          this.saveError.set(
            validationMessage ||
              err.error?.detail ||
              err.error?.title ||
              'Unable to assign barcode.'
          );
          this.isSaving.set(false);
        },
      });
  }
}
